import {ICalendarDate} from "@pg-design/calendar-module";
import {isNumber} from "@pg-mono/nodash";
import {UserComEventNames} from "@pg-mono/user-com";
import {Currency} from "@pg-rp/common-types";

import {getClientStore} from "../../../client/utils/get_client_store";
import {afterPostApplicationActions, resetApplicationPost} from "../../application/actions/application_form_actions";
import {IApplicationAdditionalQuestionTypes} from "../../application/components/application_form/ApplicationFormContext";
import {IApplicationAnalyticsData, sendApplicationHit} from "../../application/tracking/tracking";
import {IApplication} from "../../application/types/IApplication";
import {IApplicationFormValues} from "../../application/types/IApplicationFormValues";
import {IApplicationOptions} from "../../application/types/IApplicationOptions";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {handlePostApplicationResponse} from "../../application/utils/handle_post_application_response";
import {addOffersToStorageSentApplication} from "../../application/utils/manage_storage_sent_applications";
import {addPropertiesToStorageSentApplication} from "../../application/utils/property_application_storage";
import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {addAppliedOffers} from "../../offer/map/actions/add_applied_offers";
import {hitGTMEcommercePurchase} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_purchase";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {IAppDispatch} from "../../store/store";
import {awinHit} from "../../tracking/awin_tracking/awin_conversion_tag";
import {facebookPixelPurchase} from "../../tracking/facebook_pixel/facebook_pixel_purchase";
import {gtmEventApplicationRegister} from "../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {patchUserProfileFromApplication} from "../../user/actions/patch_user_profile_from_application";
import {IUserProfile} from "../../user/types/IUserProfile";
import {ViewType} from "../../view_type/ViewType";
import {createApplicationApi} from "../api/create_application";
import {gtmInquiryOnApplicationSuccess} from "../tracking/gtm_inquiry_on_application_success";
import {hitLeadSuccess} from "../tracking/hit_lead_success";
import {IHitApplicationData} from "../types/IHitApplicationData";
import {ILeadModalLeadType} from "../types/ILeadModalLeadType";
import {ILeadType} from "../types/ILeadType";
import {IPostApplicationPayload} from "../types/IPostApplicationPayload";
import {IRealEstateIds} from "../types/IRealEstateIds";
import {getParsedLeadFormValues} from "./get_parsed_lead_form_values";
import {lead} from "./lead";
import {updateUserComAfterLead} from "./update_user_com_after_lead";

export type ISendLeadParams = {
    storeHash: string;
    leadData: IHitApplicationData;
    leadFormValues: IApplicationFormValues;
    leadMeta: ILeadMeta;
    isAuthenticated: boolean;
    userProfileData?: IUserProfile | null;
    senderState: {
        /**
         * TODO generatedMessage is not being generate in Application anymore,
         * but i'm not removing it since it's used as an input in other usages
         */
        generatedMessage: string | null;
        questions: IApplicationAdditionalQuestionTypes[];
    };
    analyticsData: IApplicationAnalyticsData;
    analyticsParams?: {
        customSourceSection?: string;
        disableGtmInquiry?: boolean;
    };
    affiliation?: string;
    realEstateIds?: IRealEstateIds;
    postOptions?: IApplicationOptions;
    creditData?: {price: number; contribution: number; period: number};
    onSendSuccess?: (response: IApplication) => void;
    leadType: ILeadModalLeadType;
    dispatch: IAppDispatch;
};

export interface ILeadMeta {
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
    locationHash: string;
    viewType: ViewType | null;
}

export async function sendLead(params: ISendLeadParams) {
    const {
        storeHash,
        leadFormValues,
        leadMeta,
        leadData,
        isAuthenticated,
        senderState,
        analyticsData,
        analyticsParams,
        affiliation,
        postOptions,
        onSendSuccess,
        realEstateIds,
        userProfileData,
        creditData,
        leadType,
        dispatch
    } = params;

    const {offer, property} = leadData;

    const leadPayload = getLeadPayloadData({
        leadFormValues,
        leadMeta,
        offer,
        realEstateIds,
        creditData
    });

    const applicationResponse = await dispatch(createApplicationApi.endpoints.createApplication.initiate(leadPayload));
    const sentLeadRes = postOptions && (await handlePostApplicationResponse(applicationResponse, 1, dispatch, leadPayload, postOptions));

    if (sentLeadRes && onSendSuccess) {
        onSendSuccess(sentLeadRes);
    }

    if (sentLeadRes) {
        const {generatedMessage, questions} = senderState;

        await handleSendSuccess({
            response: sentLeadRes,
            offer: offer?.detail,
            property: property?.detail,
            isAuthenticated,
            storeHash,
            userProfileData
        });

        handleLeadTracking({
            name: leadFormValues.name,
            /*
                TODO: Create separate interface for applicationPost, so we won't have to use Partial<IApplication>
                    this will enable to remove "as string" below and will be a better approach in general
                    Application serializer to use for that - python-pg/-/blob/master/rpm/applications/serializers.py
             */
            phone: leadPayload.phone as string,
            email: leadFormValues.email,
            user_id: userProfileData?.uuid || null,
            rooms: leadFormValues.rooms,
            generatedMessage,
            questions,
            analyticsData,
            analyticsParams,
            originalText: leadFormValues.text || "",
            leadResponse: sentLeadRes,
            leadData,
            leadMeta,
            leadType: lead.getLeadType(realEstateIds?.vendorId || null, realEstateIds?.offerId || null, realEstateIds?.propertyId || null),
            leadModalType: leadType,
            affiliation,
            negotiation: leadFormValues.negotiation,
            meetingDay: leadFormValues.meetingDay
        });

        updateUserComAfterLead({
            eventName: UserComEventNames.UserLead,
            sentLead: {
                email: leadFormValues.email,
                phone: leadPayload.phone as string
            },
            userProfile: userProfileData
        });

        awinHit({
            applicationId: sentLeadRes.uuid,
            currency: offer?.detail?.currency || property?.detail?.currency || Currency.PLN
        });
    }

    return sentLeadRes;
}

interface IGetLeadPayloadParams {
    leadFormValues: IApplicationFormValues;
    leadMeta: ILeadMeta;
    offer: IHitApplicationData["offer"];
    realEstateIds?: IRealEstateIds;
    creditData?: {price: number; contribution: number; period: number};
}

function getLeadPayloadData(params: IGetLeadPayloadParams) {
    const {leadFormValues, leadMeta, offer, realEstateIds, creditData} = params;
    const {source, sourceSection} = leadMeta;

    const parsedLeadFormValues = getParsedLeadFormValues(leadFormValues);

    const applicationData: IPostApplicationPayload = {
        source,
        source_section: sourceSection,
        ...parsedLeadFormValues
    };

    if (creditData) {
        applicationData.financing_services = true;
        applicationData.term_of_the_loan = creditData.period;
        applicationData.calculator_estimated_property_price = creditData.price;
        applicationData.own_contribution = creditData.contribution;
    }

    if (realEstateIds && realEstateIds.salesOfficeId) {
        // variant: 1 disables backend validation of 'meeting_time_preference' field
        applicationData.variant = 1;
        applicationData.sales_office = realEstateIds.salesOfficeId;
    }

    if (realEstateIds && isNumber(realEstateIds.propertyId)) {
        applicationData.property = realEstateIds.propertyId;
    } else if (realEstateIds && isNumber(realEstateIds.offerId)) {
        applicationData.offer = realEstateIds.offerId;
    } else if (realEstateIds && isNumber(realEstateIds.vendorId)) {
        applicationData.vendor = realEstateIds.vendorId;
    }

    if (offer?.detail?.configuration?.fastcall_uid) {
        applicationData.fastcall = true;
    }

    return applicationData;
}

async function handleSendSuccess(props: {
    response: IApplication;
    offer?: Pick<IOfferDetailApplication, "id" | "geo_point" | "region">;
    property?: Pick<IPropertyDetailApplication, "id">;
    isAuthenticated: boolean;
    storeHash: string;
    userProfileData?: IUserProfile | null;
}) {
    const store = getClientStore();

    if (!store) {
        throw new Error("sendLead: store is not available");
    }

    afterPostApplicationActions(props.storeHash, props.response)(store.dispatch);

    if (props.offer) {
        addOffersToStorageSentApplication([
            {
                id: props.offer.id,
                coordinates: props.offer.geo_point.coordinates,
                country: props.offer.region.country,
                regionId: props.offer.region.parent?.id
            }
        ]);
    }

    if (props.property) {
        addPropertiesToStorageSentApplication([props.property.id]);
    }

    // user was created when submitting the application - try to log in
    if (props.response.user_created) {
        store.dispatch(
            authSucceeded({
                type: AuthActionType.REGISTRATION,
                userSource: UserSource.APPLICATION,
                registrationMethod: "email"
            })
        );
    }
    // try to update logged-in user data
    if (props.isAuthenticated && props.userProfileData && !props.response.user_created) {
        await store.dispatch(patchUserProfileFromApplication(props.userProfileData, props.response));
    }

    if (props.isAuthenticated) {
        store.dispatch(addAppliedOffers([props.response.offer]));
    }

    // TODO Why we're resetting application post here? It means that after posting it won't be available in redux
    store.dispatch(resetApplicationPost(props.storeHash));
}

/**
 * Handles tracking after successful lead post/submit
 *
 * @param trackingParams
 * questions - form questions, but from component state, not the form itself
 * generatedMessage - a "rotating" message, but from component state, not the form itself
 * originalText - not modified text, taken directly from form values
 */
function handleLeadTracking(trackingParams: {
    name: string;
    phone: string;
    email: string;
    user_id: string | null;
    rooms?: number[];
    questions: IApplicationAdditionalQuestionTypes[];
    generatedMessage: string | null;
    originalText: string;
    analyticsData: IApplicationAnalyticsData;
    analyticsParams?: ISendLeadParams["analyticsParams"];
    leadResponse: IApplication;
    leadData: IHitApplicationData;
    leadMeta: ILeadMeta;
    leadType: ILeadType;
    leadModalType: ILeadModalLeadType;
    affiliation?: string;
    negotiation?: number | null;
    meetingDay?: ICalendarDate;
}) {
    const {
        name,
        phone,
        email,
        rooms,
        generatedMessage,
        questions,
        analyticsData,
        analyticsParams,
        originalText,
        leadResponse,
        leadData,
        leadMeta,
        leadModalType,
        affiliation,
        negotiation,
        meetingDay
    } = trackingParams;
    const {sourceSection, locationHash, viewType} = leadMeta;
    const phoneWithoutSpace = phone.replace(" ", "");

    if (generatedMessage && generatedMessage !== originalText) {
        sendApplicationHit(questions, name, phoneWithoutSpace, email, analyticsData, generatedMessage, originalText);
    } else {
        sendApplicationHit(questions, name, phoneWithoutSpace, email, analyticsData, generatedMessage ? generatedMessage : "");
    }

    hitLeadSuccess({
        locationHash,
        viewType,
        rooms,
        leadResponse,
        sourceSection,
        leadData
    });
    let eventLabel = "zapytaj";
    if (negotiation) {
        eventLabel = "negocjuj cenę";
    }
    if (meetingDay) {
        eventLabel = "umów spotkanie";
    }
    hitGtmAfterSuccess(leadResponse, leadData, leadModalType, viewType, affiliation, analyticsParams, eventLabel);

    facebookPixelPurchase(leadData);
}

function hitGtmAfterSuccess(
    leadResponse: IApplication,
    leadData: IHitApplicationData,
    leadType: ILeadModalLeadType,
    viewType: ViewType | null,
    affiliation?: string,
    analyticsParams?: ISendLeadParams["analyticsParams"],
    eventLabel?: string
) {
    if (!leadData) {
        throw new Error("hitGtmAfterSuccess: application data should be filled already");
    }

    if (!analyticsParams?.disableGtmInquiry) {
        gtmInquiryOnApplicationSuccess(leadResponse, leadData, viewType, leadType, false, affiliation, analyticsParams?.customSourceSection, eventLabel);
        hitGTMEcommercePurchase(leadData, viewType, leadType, leadResponse.source_section, leadResponse);
    }

    if (leadResponse.user_created) {
        gtmEventApplicationRegister();
    }
}
import {ICalendarDate} from "@pg-design/calendar-module";
import {isNumber} from "@pg-mono/nodash";
import {UserComEventNames} from "@pg-mono/user-com";
import {Currency} from "@pg-rp/common-types";

import {getClientStore} from "../../../client/utils/get_client_store";
import {afterPostApplicationActions, resetApplicationPost} from "../../application/actions/application_form_actions";
import {IApplicationAdditionalQuestionTypes} from "../../application/components/application_form/ApplicationFormContext";
import {IApplicationAnalyticsData, sendApplicationHit} from "../../application/tracking/tracking";
import {IApplication} from "../../application/types/IApplication";
import {IApplicationFormValues} from "../../application/types/IApplicationFormValues";
import {IApplicationOptions} from "../../application/types/IApplicationOptions";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {handlePostApplicationResponse} from "../../application/utils/handle_post_application_response";
import {addOffersToStorageSentApplication} from "../../application/utils/manage_storage_sent_applications";
import {addPropertiesToStorageSentApplication} from "../../application/utils/property_application_storage";
import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {addAppliedOffers} from "../../offer/map/actions/add_applied_offers";
import {hitGTMEcommercePurchase} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_purchase";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {IAppDispatch} from "../../store/store";
import {awinHit} from "../../tracking/awin_tracking/awin_conversion_tag";
import {facebookPixelPurchase} from "../../tracking/facebook_pixel/facebook_pixel_purchase";
import {gtmEventApplicationRegister} from "../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {patchUserProfileFromApplication} from "../../user/actions/patch_user_profile_from_application";
import {IUserProfile} from "../../user/types/IUserProfile";
import {ViewType} from "../../view_type/ViewType";
import {createApplicationApi} from "../api/create_application";
import {gtmInquiryOnApplicationSuccess} from "../tracking/gtm_inquiry_on_application_success";
import {hitLeadSuccess} from "../tracking/hit_lead_success";
import {IHitApplicationData} from "../types/IHitApplicationData";
import {ILeadModalLeadType} from "../types/ILeadModalLeadType";
import {ILeadType} from "../types/ILeadType";
import {IPostApplicationPayload} from "../types/IPostApplicationPayload";
import {IRealEstateIds} from "../types/IRealEstateIds";
import {getParsedLeadFormValues} from "./get_parsed_lead_form_values";
import {lead} from "./lead";
import {updateUserComAfterLead} from "./update_user_com_after_lead";

export type ISendLeadParams = {
    storeHash: string;
    leadData: IHitApplicationData;
    leadFormValues: IApplicationFormValues;
    leadMeta: ILeadMeta;
    isAuthenticated: boolean;
    userProfileData?: IUserProfile | null;
    senderState: {
        /**
         * TODO generatedMessage is not being generate in Application anymore,
         * but i'm not removing it since it's used as an input in other usages
         */
        generatedMessage: string | null;
        questions: IApplicationAdditionalQuestionTypes[];
    };
    analyticsData: IApplicationAnalyticsData;
    analyticsParams?: {
        customSourceSection?: string;
        disableGtmInquiry?: boolean;
    };
    affiliation?: string;
    realEstateIds?: IRealEstateIds;
    postOptions?: IApplicationOptions;
    creditData?: {price: number; contribution: number; period: number};
    onSendSuccess?: (response: IApplication) => void;
    leadType: ILeadModalLeadType;
    dispatch: IAppDispatch;
};

export interface ILeadMeta {
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
    locationHash: string;
    viewType: ViewType | null;
}

export async function sendLead(params: ISendLeadParams) {
    const {
        storeHash,
        leadFormValues,
        leadMeta,
        leadData,
        isAuthenticated,
        senderState,
        analyticsData,
        analyticsParams,
        affiliation,
        postOptions,
        onSendSuccess,
        realEstateIds,
        userProfileData,
        creditData,
        leadType,
        dispatch
    } = params;

    const {offer, property} = leadData;

    const leadPayload = getLeadPayloadData({
        leadFormValues,
        leadMeta,
        offer,
        realEstateIds,
        creditData
    });

    const applicationResponse = await dispatch(createApplicationApi.endpoints.createApplication.initiate(leadPayload));
    const sentLeadRes = postOptions && (await handlePostApplicationResponse(applicationResponse, 1, dispatch, leadPayload, postOptions));

    if (sentLeadRes && onSendSuccess) {
        onSendSuccess(sentLeadRes);
    }

    if (sentLeadRes) {
        const {generatedMessage, questions} = senderState;

        await handleSendSuccess({
            response: sentLeadRes,
            offer: offer?.detail,
            property: property?.detail,
            isAuthenticated,
            storeHash,
            userProfileData
        });

        handleLeadTracking({
            name: leadFormValues.name,
            /*
                TODO: Create separate interface for applicationPost, so we won't have to use Partial<IApplication>
                    this will enable to remove "as string" below and will be a better approach in general
                    Application serializer to use for that - python-pg/-/blob/master/rpm/applications/serializers.py
             */
            phone: leadPayload.phone as string,
            email: leadFormValues.email,
            user_id: userProfileData?.uuid || null,
            rooms: leadFormValues.rooms,
            generatedMessage,
            questions,
            analyticsData,
            analyticsParams,
            originalText: leadFormValues.text || "",
            leadResponse: sentLeadRes,
            leadData,
            leadMeta,
            leadType: lead.getLeadType(realEstateIds?.vendorId || null, realEstateIds?.offerId || null, realEstateIds?.propertyId || null),
            leadModalType: leadType,
            affiliation,
            negotiation: leadFormValues.negotiation,
            meetingDay: leadFormValues.meetingDay
        });

        updateUserComAfterLead({
            eventName: UserComEventNames.UserLead,
            sentLead: {
                email: leadFormValues.email,
                phone: leadPayload.phone as string
            },
            userProfile: userProfileData
        });

        awinHit({
            applicationId: sentLeadRes.uuid,
            currency: offer?.detail?.currency || property?.detail?.currency || Currency.PLN
        });
    }

    return sentLeadRes;
}

interface IGetLeadPayloadParams {
    leadFormValues: IApplicationFormValues;
    leadMeta: ILeadMeta;
    offer: IHitApplicationData["offer"];
    realEstateIds?: IRealEstateIds;
    creditData?: {price: number; contribution: number; period: number};
}

function getLeadPayloadData(params: IGetLeadPayloadParams) {
    const {leadFormValues, leadMeta, offer, realEstateIds, creditData} = params;
    const {source, sourceSection} = leadMeta;

    const parsedLeadFormValues = getParsedLeadFormValues(leadFormValues);

    const applicationData: IPostApplicationPayload = {
        source,
        source_section: sourceSection,
        ...parsedLeadFormValues
    };

    if (creditData) {
        applicationData.financing_services = true;
        applicationData.term_of_the_loan = creditData.period;
        applicationData.calculator_estimated_property_price = creditData.price;
        applicationData.own_contribution = creditData.contribution;
    }

    if (realEstateIds && realEstateIds.salesOfficeId) {
        // variant: 1 disables backend validation of 'meeting_time_preference' field
        applicationData.variant = 1;
        applicationData.sales_office = realEstateIds.salesOfficeId;
    }

    if (realEstateIds && isNumber(realEstateIds.propertyId)) {
        applicationData.property = realEstateIds.propertyId;
    } else if (realEstateIds && isNumber(realEstateIds.offerId)) {
        applicationData.offer = realEstateIds.offerId;
    } else if (realEstateIds && isNumber(realEstateIds.vendorId)) {
        applicationData.vendor = realEstateIds.vendorId;
    }

    if (offer?.detail?.configuration?.fastcall_uid) {
        applicationData.fastcall = true;
    }

    return applicationData;
}

async function handleSendSuccess(props: {
    response: IApplication;
    offer?: Pick<IOfferDetailApplication, "id" | "geo_point" | "region">;
    property?: Pick<IPropertyDetailApplication, "id">;
    isAuthenticated: boolean;
    storeHash: string;
    userProfileData?: IUserProfile | null;
}) {
    const store = getClientStore();

    if (!store) {
        throw new Error("sendLead: store is not available");
    }

    afterPostApplicationActions(props.storeHash, props.response)(store.dispatch);

    if (props.offer) {
        addOffersToStorageSentApplication([
            {
                id: props.offer.id,
                coordinates: props.offer.geo_point.coordinates,
                country: props.offer.region.country,
                regionId: props.offer.region.parent?.id
            }
        ]);
    }

    if (props.property) {
        addPropertiesToStorageSentApplication([props.property.id]);
    }

    // user was created when submitting the application - try to log in
    if (props.response.user_created) {
        store.dispatch(
            authSucceeded({
                type: AuthActionType.REGISTRATION,
                userSource: UserSource.APPLICATION,
                registrationMethod: "email"
            })
        );
    }
    // try to update logged-in user data
    if (props.isAuthenticated && props.userProfileData && !props.response.user_created) {
        await store.dispatch(patchUserProfileFromApplication(props.userProfileData, props.response));
    }

    if (props.isAuthenticated) {
        store.dispatch(addAppliedOffers([props.response.offer]));
    }

    // TODO Why we're resetting application post here? It means that after posting it won't be available in redux
    store.dispatch(resetApplicationPost(props.storeHash));
}

/**
 * Handles tracking after successful lead post/submit
 *
 * @param trackingParams
 * questions - form questions, but from component state, not the form itself
 * generatedMessage - a "rotating" message, but from component state, not the form itself
 * originalText - not modified text, taken directly from form values
 */
function handleLeadTracking(trackingParams: {
    name: string;
    phone: string;
    email: string;
    user_id: string | null;
    rooms?: number[];
    questions: IApplicationAdditionalQuestionTypes[];
    generatedMessage: string | null;
    originalText: string;
    analyticsData: IApplicationAnalyticsData;
    analyticsParams?: ISendLeadParams["analyticsParams"];
    leadResponse: IApplication;
    leadData: IHitApplicationData;
    leadMeta: ILeadMeta;
    leadType: ILeadType;
    leadModalType: ILeadModalLeadType;
    affiliation?: string;
    negotiation?: number | null;
    meetingDay?: ICalendarDate;
}) {
    const {
        name,
        phone,
        email,
        rooms,
        generatedMessage,
        questions,
        analyticsData,
        analyticsParams,
        originalText,
        leadResponse,
        leadData,
        leadMeta,
        leadModalType,
        affiliation,
        negotiation,
        meetingDay
    } = trackingParams;
    const {sourceSection, locationHash, viewType} = leadMeta;
    const phoneWithoutSpace = phone.replace(" ", "");

    if (generatedMessage && generatedMessage !== originalText) {
        sendApplicationHit(questions, name, phoneWithoutSpace, email, analyticsData, generatedMessage, originalText);
    } else {
        sendApplicationHit(questions, name, phoneWithoutSpace, email, analyticsData, generatedMessage ? generatedMessage : "");
    }

    hitLeadSuccess({
        locationHash,
        viewType,
        rooms,
        leadResponse,
        sourceSection,
        leadData
    });
    let eventLabel = "zapytaj";
    if (negotiation) {
        eventLabel = "negocjuj cenę";
    }
    if (meetingDay) {
        eventLabel = "umów spotkanie";
    }
    hitGtmAfterSuccess(leadResponse, leadData, leadModalType, viewType, affiliation, analyticsParams, eventLabel);

    facebookPixelPurchase(leadData);
}

function hitGtmAfterSuccess(
    leadResponse: IApplication,
    leadData: IHitApplicationData,
    leadType: ILeadModalLeadType,
    viewType: ViewType | null,
    affiliation?: string,
    analyticsParams?: ISendLeadParams["analyticsParams"],
    eventLabel?: string
) {
    if (!leadData) {
        throw new Error("hitGtmAfterSuccess: application data should be filled already");
    }

    if (!analyticsParams?.disableGtmInquiry) {
        gtmInquiryOnApplicationSuccess(leadResponse, leadData, viewType, leadType, false, affiliation, analyticsParams?.customSourceSection, eventLabel);
        hitGTMEcommercePurchase(leadData, viewType, leadType, leadResponse.source_section, leadResponse);
    }

    if (leadResponse.user_created) {
        gtmEventApplicationRegister();
    }
}
